<script>
import Business from "@/services/Business";
import Swal from "sweetalert2";

const DEFAULT_BACKGROUND_COLOR = "#e8edf2";

export default {
  name: "ModalEditBusinessWall",
  props: {
    businessWallCompanyDetails: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      editableBusinessWall: {},
      originalBackgroundColor: DEFAULT_BACKGROUND_COLOR,
      defaultBackgroundColor: DEFAULT_BACKGROUND_COLOR,
      saving: false,
    };
  },
  watch: {
    businessWallCompanyDetails: {
      handler(newVal) {
        if (!newVal) {
          return;
        }
        const backgroundColor = newVal.background_color || DEFAULT_BACKGROUND_COLOR;
        this.editableBusinessWall = {
          ...newVal,
          order: Number(newVal.order) || 0,
          is_highlighted: Number(newVal.is_highlighted) === 1 ? 1 : 0,
          background_color: backgroundColor,
        };
        this.originalBackgroundColor = backgroundColor;
      },
      immediate: true,
    },
  },
  methods: {
    normalizeColor(value) {
      const color = String(value || "").trim();
      return color || DEFAULT_BACKGROUND_COLOR;
    },
    async saveChanges() {
      if (this.saving || !this.businessWallCompanyDetails?.company_id) {
        return;
      }

      const companyId = this.businessWallCompanyDetails.company_id;
      const wallPayload = {
        order: Number(this.editableBusinessWall.order),
        is_highlighted: Number(this.editableBusinessWall.is_highlighted) === 1 ? 1 : 0,
      };
      const nextBackgroundColor = this.normalizeColor(
        this.editableBusinessWall.background_color
      );
      const backgroundChanged =
        this.normalizeColor(this.originalBackgroundColor) !== nextBackgroundColor;

      try {
        this.saving = true;

        const wallResponse = await Business.updateBusinessWall(companyId, wallPayload);
        if (!wallResponse?.data?.data?.success) {
          throw new Error("Failed to update business wall");
        }

        if (backgroundChanged) {
          await Business.updateBusinessCompany(companyId, {
            background_color: nextBackgroundColor,
          });
        }

        this.$emit("onRefresh");
        this.$bvModal.hide("edit_business_wall");
        Swal.fire("Success", "Business wall updated successfully.", "success");
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          "Error saving changes. Please try again.";
        Swal.fire(
          "Fail!",
          Array.isArray(message) ? message.join(", ") : message,
          "warning"
        );
      } finally {
        this.saving = false;
      }
    },
    closeModal() {
      if (this.saving) {
        return;
      }
      this.$bvModal.hide("edit_business_wall");
    },
  },
};
</script>

<template>
  <b-modal
    id="edit_business_wall"
    title="Edit Business Wall"
    size="md"
    centered
    @hidden="closeModal"
  >
    <div v-if="editableBusinessWall.company_id">
      <b-form-group label="Company">
        <b-form-input :value="editableBusinessWall.name" disabled />
      </b-form-group>

      <b-form-group label="Order">
        <b-form-input
          v-model.number="editableBusinessWall.order"
          type="number"
          min="0"
        />
      </b-form-group>

      <b-form-group label="Is Highlighted">
        <b-form-select
          v-model="editableBusinessWall.is_highlighted"
          :options="[
            { value: 0, text: 'No' },
            { value: 1, text: 'Yes' },
          ]"
        />
      </b-form-group>

      <b-form-group label="Background Color">
        <b-form-input
          v-model="editableBusinessWall.background_color"
          type="color"
          class="form-control form-control-color"
        />
      </b-form-group>

      <b-form-group label="Preview">
        <div
          class="business-wall-edit-preview"
          :style="{
            backgroundColor:
              editableBusinessWall.background_color || defaultBackgroundColor,
          }"
        >
          <img
            v-if="editableBusinessWall.logo_url"
            :src="editableBusinessWall.logo_url"
            :alt="editableBusinessWall.name"
            class="business-wall-edit-preview__logo"
          />
        </div>
      </b-form-group>
    </div>

    <template #modal-footer>
      <b-button variant="light" :disabled="saving" @click="closeModal">
        Cancel
      </b-button>
      <b-button variant="primary" :disabled="saving" @click="saveChanges">
        <b-spinner v-if="saving" small class="me-1"></b-spinner>
        {{ saving ? "Saving..." : "Save Changes" }}
      </b-button>
    </template>
  </b-modal>
</template>

<style scoped>
.business-wall-edit-preview {
  width: 120px;
  height: 72px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.business-wall-edit-preview__logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
</style>
